import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import cargaApi, { CARGA_TOKEN_KEY } from "@/lib/cargaApi";
import { DICT } from "./i18n";

const Ctx = createContext(null);
const LANG_KEY = "carga_lang";

const CARGA_ICONS = { "icon": "/carga/favicon-32.png", "apple-touch-icon": "/carga/apple-touch-icon.png", "shortcut icon": "/carga/favicon.ico" };

function useCargaBranding() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "MFG Carga · Tablero de cargas de Colombia";
    const links = [...document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"], link[rel="shortcut icon"]')];
    const saved = links.map((l) => [l, l.getAttribute("href")]);
    links.forEach((l) => l.setAttribute("href", CARGA_ICONS[l.getAttribute("rel")] || "/carga/favicon-32.png"));
    const theme = document.querySelector('meta[name="theme-color"]');
    const prevTheme = theme?.getAttribute("content");
    theme?.setAttribute("content", "#07090E");
    return () => { document.title = prevTitle; saved.forEach(([l, h]) => h && l.setAttribute("href", h)); if (theme && prevTheme) theme.setAttribute("content", prevTheme); };
  }, []);
}

export function CargaProvider({ children }) {
  useCargaBranding();
  const [state, setState] = useState({ loading: !!localStorage.getItem(CARGA_TOKEN_KEY), user: null, company: null, entitlement: null, unread: 0 });
  const [lang, setLangState] = useState(localStorage.getItem(LANG_KEY) || "es");
  const [catalog, setCatalog] = useState(null);
  const refresh = useCallback(async () => {
    if (!localStorage.getItem(CARGA_TOKEN_KEY)) { setState({ loading: false, user: null, company: null, entitlement: null, unread: 0 }); return; }
    try { const { data } = await cargaApi.get("/auth/me"); setState({ loading: false, ...data }); if (data.user?.language) setLangState(data.user.language); }
    catch { localStorage.removeItem(CARGA_TOKEN_KEY); setState({ loading: false, user: null, company: null, entitlement: null, unread: 0 }); }
  }, []);
  useEffect(() => { refresh(); cargaApi.get("/auth/catalog").then((r) => setCatalog(r.data)).catch(() => {}); const out = () => setState({ loading: false, user: null, company: null, entitlement: null, unread: 0 }); window.addEventListener("carga:logout", out); return () => window.removeEventListener("carga:logout", out); }, [refresh]);
  const login = (data) => { localStorage.setItem(CARGA_TOKEN_KEY, data.token); setState({ loading: false, user: data.user, company: data.company, entitlement: data.entitlement, unread: 0 }); if (data.user?.language) setLangState(data.user.language); setTimeout(refresh, 300); };
  useEffect(() => { if (!state.user) return; const i = setInterval(refresh, 60000); return () => clearInterval(i); }, [state.user, refresh]);
  const logout = () => { localStorage.removeItem(CARGA_TOKEN_KEY); setState({ loading: false, user: null, company: null, entitlement: null, unread: 0 }); };
  const setLang = (l) => { setLangState(l); localStorage.setItem(LANG_KEY, l); if (state.user) cargaApi.patch("/auth/language", { language: l }).catch(() => {}); };
  const t = useCallback((k) => DICT[lang]?.[k] ?? DICT.es[k] ?? k, [lang]);
  return <Ctx.Provider value={{ ...state, docTypes: state.doc_types, catalog, lang, setLang, t, login, logout, refresh }}>{children}</Ctx.Provider>;
}

export const useCarga = () => useContext(Ctx);

export function LangToggle({ dark = false }) {
  const { lang, setLang } = useCarga();
  return (
    <div className={`inline-flex rounded-full border text-[11px] font-bold overflow-hidden ${dark ? "border-white/20" : "border-black/15"}`} data-testid="carga-lang-toggle">
      {["es", "en"].map((l) => (
        <button key={l} onClick={() => setLang(l)} data-testid={`carga-lang-${l}`} className={`px-2.5 sm:px-3 py-1 uppercase !min-h-0 ${lang === l ? "bg-[#FCD116] text-black" : dark ? "text-white/70" : "text-neutral-600"}`}>{l}</button>
      ))}
    </div>
  );
}
